// RESONATE_TRACKING_PIXELS_V1
// Injects Meta Pixel, Google Ads conversion tag, Reddit Pixel, LinkedIn Insight Tag
// and Microsoft Clarity into the document head. Each pixel is conditionally loaded
// based on the presence of its env var - set them in /app/frontend/.env to activate.
//
// Required env vars (all optional - pixel skipped when missing):
//   REACT_APP_META_PIXEL_ID        e.g. 1234567890123456
//   REACT_APP_GOOGLE_ADS_ID        e.g. AW-1234567890   (no /label here)
//   REACT_APP_REDDIT_PIXEL_ID      e.g. a2_abc123
//   REACT_APP_LINKEDIN_PARTNER_ID  e.g. 1234567
//   REACT_APP_MS_CLARITY_ID        e.g. abcd1234ef
//
// GA4 (G-3ZS92PSRH0) is hard-loaded in index.html.

import { useEffect } from "react";

const injected = new Set();

const injectScript = (id, html, attrs = {}) => {
  if (injected.has(id) || document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  s.text = html;
  document.head.appendChild(s);
  injected.add(id);
};

const injectExternal = (id, src, attrs = {}) => {
  if (injected.has(id) || document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
  injected.add(id);
};

const injectNoscriptImg = (id, src) => {
  if (injected.has(id) || document.getElementById(id)) return;
  const ns = document.createElement("noscript");
  ns.id = id;
  const img = document.createElement("img");
  img.height = "1";
  img.width = "1";
  img.style.display = "none";
  img.src = src;
  ns.appendChild(img);
  document.body.appendChild(ns);
  injected.add(id);
};

const loadMetaPixel = (id) => {
  injectScript(
    "meta-pixel",
    `!function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${id}');
    fbq('track', 'PageView');
    fbq('track', 'ViewContent');`
  );
  injectNoscriptImg(
    "meta-pixel-noscript",
    `https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1`
  );
};

const loadGoogleAds = (id) => {
  // id should be the AW-XXXXXXX conversion ID
  injectExternal("google-ads-gtag", `https://www.googletagmanager.com/gtag/js?id=${id}`);
  injectScript(
    "google-ads-config",
    `window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${id}');`
  );
};

const loadRedditPixel = (id) => {
  injectScript(
    "reddit-pixel",
    `!function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
    rdt('init','${id}');
    rdt('track', 'PageVisit');`
  );
};

const loadLinkedInInsight = (partnerId) => {
  injectScript(
    "linkedin-insight-config",
    `_linkedin_partner_id = "${partnerId}";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(_linkedin_partner_id);`
  );
  injectScript(
    "linkedin-insight",
    `(function(l) {
    if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
    window.lintrk.q=[]}
    var s = document.getElementsByTagName("script")[0];
    var b = document.createElement("script");
    b.type = "text/javascript";b.async = true;
    b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    s.parentNode.insertBefore(b, s);})(window.lintrk);`
  );
  injectNoscriptImg(
    "linkedin-insight-noscript",
    `https://px.ads.linkedin.com/collect/?pid=${partnerId}&fmt=gif`
  );
};

const loadMsClarity = (id) => {
  injectScript(
    "ms-clarity",
    `(function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${id}");`
  );
};

export default function TrackingPixels() {
  useEffect(() => {
    const env = process.env;
    const meta = env.REACT_APP_META_PIXEL_ID;
    const gAds = env.REACT_APP_GOOGLE_ADS_ID;
    const reddit = env.REACT_APP_REDDIT_PIXEL_ID;
    const linkedin = env.REACT_APP_LINKEDIN_PARTNER_ID;
    const clarity = env.REACT_APP_MS_CLARITY_ID;

    if (meta) loadMetaPixel(meta);
    if (gAds) loadGoogleAds(gAds);
    if (reddit) loadRedditPixel(reddit);
    if (linkedin) loadLinkedInInsight(linkedin);
    if (clarity) loadMsClarity(clarity);
  }, []);

  return null;
}

// Helper: fire a Meta Pixel "Lead" event (call on /thank-you or signup confirmation).
export const trackMetaLead = (value, currency = "USD") => {
  if (typeof window === "undefined" || !window.fbq) return;
  window.fbq("track", "Lead", value ? { value, currency } : undefined);
};

// Helper: fire a Google Ads conversion. conversionLabel is the /xxx part of AW-XXX/yyy.
export const trackGoogleAdsConversion = (conversionLabel, value, currency = "USD") => {
  if (typeof window === "undefined" || !window.gtag) return;
  const gAdsId = process.env.REACT_APP_GOOGLE_ADS_ID;
  if (!gAdsId || !conversionLabel) return;
  window.gtag("event", "conversion", {
    send_to: `${gAdsId}/${conversionLabel}`,
    value,
    currency,
  });
};

// Helper: fire a Reddit conversion event.
export const trackRedditSignup = (value, currency = "USD") => {
  if (typeof window === "undefined" || !window.rdt) return;
  window.rdt("track", "SignUp", value ? { value, currency } : undefined);
};
